import { Common, color } from '@stockbit/ui';
import { FC, useEffect, useMemo } from 'react';
import Link from 'next/link';
import shallow from 'zustand/shallow';

import If from 'global/ErrorHandlers/If';
import Image from 'global/Image';
import { getColorBasedOnNumber } from 'utils/general';
import { formatNumber, formatPercentage } from 'utils/number';
import useEmittenInfoStore from 'global/Securities/stores/useEmittenInfoStore';

import useLivePriceUpdater from 'global/Securities/hooks/useLivePriceUpdater';

import CompanyLabel from './CompanyLabel';
import {
  CompanyButton,
  CompanyCard,
  CompanySkeletonBlock,
  SymbolAvatar,
} from './styled';
import BondsCompany from './BondsCompany';

const { Text, Flex, Iconmoon } = Common;

interface CompanyHeaderType {
  symbol: string;
  showPrice: boolean;
  isBonds: boolean;
  isRedemption?: boolean;
  bondsName: string;
  onClick?(): void;
  showBondPrice?: boolean;
}

interface CompanyContainerProps {
  href: string;
  onClick?(): void;
}

const CompanyContainer: FC<CompanyContainerProps> = ({
  children,
  href,
  onClick,
}) =>
  onClick ? (
    <CompanyButton onClick={onClick}>{children}</CompanyButton>
  ) : (
    <Link href={href}>{children}</Link>
  );

const Company = ({
  symbol,
  showPrice,
  isBonds,
  isRedemption = false,
  bondsName,
  onClick,
  showBondPrice = false,
}: CompanyHeaderType) => {
  const {
    data: emittenInfo,
    isLoading,
    loadEmittenInfo,
    resetEmittenInfo,
    updateEmittenInfo,
  } = useEmittenInfoStore(
    (s) => ({
      data: s.data,
      isLoading: s.isLoading,
      loadEmittenInfo: s.loadEmittenInfo,
      resetEmittenInfo: s.resetEmittenInfo,
      updateEmittenInfo: s.updateEmittenInfo,
    }),
    shallow,
  );
  const {
    name,
    icon_url,
    formatted_price,
    change,
    percentage,
    notation,
    corp_action: corpAction,
    indexes,
    trade_type: tradeType,
    day_trade_info: dayTradeInfo,
  } = emittenInfo;

  const isEmittenEmpty = useMemo(
    () => Object.keys(emittenInfo).length === 0,
    [JSON.stringify(emittenInfo)],
  );

  useLivePriceUpdater(
    {
      symbol,
      page: 'orderlist_detail',
    },
    ({ lastprice, change: liveChange, percentage_change }) => {
      updateEmittenInfo({
        formatted_price: lastprice.toString(),
        change: liveChange.toString(),
        percentage: percentage_change,
      });
    },
  );

  useEffect(() => {
    if (symbol && !isRedemption) loadEmittenInfo(symbol);
    return () => {
      resetEmittenInfo();
    };
  }, [symbol, isRedemption]);

  if (isBonds || isRedemption) {
    return (
      <BondsCompany
        symbol={symbol}
        iconUrl={icon_url}
        showPrice={showPrice}
        bondsName={bondsName}
        showBondPrice={showBondPrice}
      />
    );
  }

  if (isLoading) return <CompanySkeletonBlock />;
  if (isEmittenEmpty) return null;

  return (
    <CompanyContainer href={`/symbol/${symbol}`} onClick={onClick}>
      <CompanyCard as="a" data-cy="trade-detail-modal-company-container">
        <Flex
          justify="space-between"
          align="center"
          width="100%"
          data-cy="trade-detail-modal-company-wrapper"
        >
          <Flex align="center" width={showPrice ? '320px' : '100%'}>
            {!icon_url ? (
              <SymbolAvatar>
                <Text color="white" family="bold" size="16px">
                  {symbol[0]}
                </Text>
              </SymbolAvatar>
            ) : (
              <Image
                src={icon_url}
                style={{ width: '40px', height: '40px', background: 'white' }}
                data-cy="trade-detail-modal-company-image"
              />
            )}
            <Flex
              direction="column"
              pl="15px"
              data-cy="trade-detail-modal-company-info"
            >
              <Flex align="center">
                <Text family="bold">{symbol}</Text>
                <CompanyLabel
                  notation={notation}
                  corpAction={corpAction}
                  indexes={indexes}
                  tradeType={tradeType}
                  dayTradeInfo={dayTradeInfo}
                />
              </Flex>
              <Text
                size="12px"
                color={color.midgray}
                mr="5px"
                mt="3px"
                ellipsis
              >
                {name}
              </Text>
            </Flex>
          </Flex>
          <If condition={showPrice}>
            <Flex direction="column" align="end">
              <Text as="div" family="bold" align="right">
                {formatted_price}
              </Text>
              <Text
                align="right"
                mt="3px"
                color={getColorBasedOnNumber(change)}
              >
                {Number(change) > 0 ? '+' : ''}
                {formatNumber(change, false)} (
                {formatPercentage(percentage, false)})
              </Text>
            </Flex>
          </If>
          <If condition={!showPrice}>
            <Iconmoon className="icon-trading_back reverse" />
          </If>
        </Flex>
      </CompanyCard>
    </CompanyContainer>
  );
};

export default Company;
